import { Announcement } from "@cryptors/api-client";
import { Alert, Box, FormControlLabel, Switch, TextField } from "@mui/material";
import { HttpError } from "@refinedev/core";
import { Create, Edit } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import { format } from "date-fns";
import { useEffect } from "react";
import { Controller } from "react-hook-form";

import { getTranslation } from "@/utils/translation";

interface AnnouncementFormValues {
  enabled: boolean;
  endDate: string | null;
  title?: string;
  message?: string;
  titleTranslation?: { value: string };
  messageTranslation?: { value: string };
}

export const AnnouncementCreateEdit = ({
  isEditing = false,
}: {
  isEditing?: boolean;
}) => {
  const {
    control,
    refineCore: { formLoading, onFinish, query },
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<Announcement, HttpError, AnnouncementFormValues>({
    defaultValues: {
      enabled: false,
      endDate: "",
      title: "",
      message: "",
    },
    mode: "onChange",
  });

  const announcement = query?.data?.data;

  useEffect(() => {
    if (!isEditing || !announcement) return;
    reset({
      enabled: announcement.enabled,
      endDate: announcement.endDate
        ? format(new Date(announcement.endDate), "yyyy-MM-dd'T'HH:mm")
        : "",
      title: getTranslation(announcement.titleTranslation, "fr")?.value ?? "",
      message:
        getTranslation(announcement.messageTranslation, "fr")?.value ?? "",
    });
  }, [announcement, isEditing, reset]);

  const onFinishHandler = (data: AnnouncementFormValues) =>
    onFinish({
      enabled: data.enabled,
      endDate: data.endDate ? new Date(data.endDate).toISOString() : null,
      titleTranslation: { value: data.title ?? "" },
      messageTranslation: { value: data.message ?? "" },
    });

  const saveButtonProps = {
    onClick: handleSubmit(onFinishHandler),
  };

  const form = (
    <Box
      component="form"
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      autoComplete="off"
    >
      <Alert severity="info">
        Le titre et le message sont saisis en français puis traduits
        automatiquement dans les autres langues de l'application. Toute
        modification crée une nouvelle version qui sera de nouveau affichée aux
        joueurs.
      </Alert>
      <Controller
        control={control}
        name="enabled"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={field.value}
                onChange={(_, checked) => field.onChange(checked)}
              />
            }
            label={field.value ? "Annonce activée" : "Annonce désactivée"}
          />
        )}
      />
      <TextField
        {...register("title", {
          required: "Ce champ est requis",
        })}
        error={!!errors.title}
        helperText={errors.title?.message}
        fullWidth
        label="Titre (français)"
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        {...register("message", {
          required: "Ce champ est requis",
        })}
        error={!!errors.message}
        helperText={errors.message?.message}
        fullWidth
        multiline
        minRows={5}
        label="Message (français)"
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        {...register("endDate")}
        error={!!errors.endDate}
        helperText={
          errors.endDate?.message ??
          "Optionnel. L'annonce disparaîtra automatiquement à cette date."
        }
        fullWidth
        type="datetime-local"
        label="Date de fin"
        slotProps={{ inputLabel: { shrink: true } }}
      />
    </Box>
  );

  return isEditing ? (
    <Edit
      title="Modifier l'annonce"
      isLoading={formLoading}
      saveButtonProps={saveButtonProps}
      canDelete
    >
      {form}
    </Edit>
  ) : (
    <Create
      title="Créer une annonce"
      isLoading={formLoading}
      saveButtonProps={saveButtonProps}
    >
      {form}
    </Create>
  );
};
